// @ts-strict-ignore
import { SetStateAction, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import {
  useUnifiedPayoutsPostAuthMutation,
  useUnifiedPayoutsPreAuthMutation,
} from 'hooks/mutations/FiatPayouts/useUnifiedPayouts'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, BeneficiaryDetails } from 'types/beneficiaries'
import { ContactPaymentInstruments } from 'types/contacts'
import { MappedErrorMessageState } from 'types/errors'
import { PayoutsV2PostAuthRequest } from 'types/payoutsV2'
import { type ErrorDetails } from 'types/types'
import { UnifiedPayoutsV2AuthResponse } from 'types/unifiedPayouts/unifiedPayoutsV2'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { SourcePayWallet } from '../../Pay/types'
import { PAYOUT_VERSIONS } from '../constants'
import { useFiatSendContext } from '../context/FiatSendContext'
import { FiatSendFormData, FiatSendFormKeys } from '../form/fiatSendFormSchema'
import { FiatSendFormProps } from '../types'
import {
  getUnifiedPayoutRequestV2,
  getUnifiedPayoutRequestV2WithContactBankAccount,
  mapPayoutErrorsToErrorDetails,
} from '../utils'

interface HandleSentryLogProps {
  errorTitle: string
  error: BaseErrorResponse
  additionalData?: object
  errorCallout?: string
  ErrorMessages?: ErrorDetails
}

interface UsePayoutSubmitProps {
  fiatBeneficiary: BeneficiaryDetails
  contactPaymentInstrument: ContactPaymentInstruments
  selectedWallet: SourcePayWallet
  formValues: FiatSendFormData
  form: FiatSendFormProps
  setStep: (step: FiatSendStep) => void
  setRequestError: (value: SetStateAction<MappedErrorMessageState>) => void
}

export const usePayoutSubmit = ({
  fiatBeneficiary,
  contactPaymentInstrument,
  selectedWallet,
  formValues,
  form,
  setStep,
  setRequestError,
}: UsePayoutSubmitProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const queryClient = useQueryClient()
  const { setTransactionId } = useFiatSendContext()

  const [authRequest, setAuthRequest] = useState<PayoutsV2PostAuthRequest>(null)
  const [submitBeneficiary, setSubmitBeneficiary] = useState(false)

  const handleSentryLog = ({
    errorTitle,
    error,
    additionalData,
    errorCallout,
    ErrorMessages,
  }: HandleSentryLogProps) => {
    const sentryTraceId = logSentryErrorAndGetTraceId(errorTitle, error, additionalData)

    setRequestError({
      handledMessage: errorCallout,
      errorCode: error?.status,
      sentryTraceId,
      show: true,
      ...(ErrorMessages && { ErrorMessages }),
    })
  }

  const { mutate: submitPayoutRequest, isPending: isSubmittingUnifiedPayoutRequest } =
    useUnifiedPayoutsPreAuthMutation()
  const { mutate: submitPayoutAuthRequest, isPending: isSubmittingUnifiedPayoutAuthRequest } =
    useUnifiedPayoutsPostAuthMutation()

  const onSubmitPayoutHandler = async () => {
    setRequestError(null)
    const payoutsPrerequest = contactPaymentInstrument
      ? getUnifiedPayoutRequestV2WithContactBankAccount({
          formValues,
          selectedWallet,
          contactPaymentInstrument,
        })
      : getUnifiedPayoutRequestV2(formValues, selectedWallet, fiatBeneficiary)

    submitPayoutRequest(
      { verificationId: formValues?.beneficiaryVerificationId, request: payoutsPrerequest },
      {
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.ERROR,
            step: FiatSendSteps.CONFIRMATION,
            apiVersion: PAYOUT_VERSIONS.UNIFIED_PAYOUT_V2,
            error,
          })
          onErrorHandler('Error from submitPayoutRequest', error, payoutsPrerequest)
        },
      }
    )
  }

  const onErrorHandler = (title: string, error: BaseErrorResponse, additionalData?: object) => {
    if (error?.status === 428) {
      setAuthRequest({
        challenge: error?.data?.challenge?.value,
        tokenResponse: formValues?.twoFactorCode,
        mfaCallbackUrl: error?.data?.callbackUrl,
      })
      form?.setValue('challengeCode', error?.data?.challenge?.value)
      setSubmitBeneficiary(true)
    } else if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form.setError(err?.parameter as FiatSendFormKeys, { message: error?.message })
      })
    } else if (error?.status === 403) {
      form.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else {
      const detailErrors = error?.data?.details?.errors
      const ErrorMessages =
        detailErrors && Object.keys(detailErrors).length
          ? mapPayoutErrorsToErrorDetails(detailErrors)
          : undefined

      handleSentryLog({
        errorTitle: title,
        error,
        additionalData,
        errorCallout: error?.data?.message || t('payoutError'),
        ErrorMessages,
      })
    }
  }

  const onSubmitPostRequest = () => {
    const payoutRequest = getUnifiedPayoutRequestV2(formValues, selectedWallet, fiatBeneficiary)
    const payoutsPostrequest: PayoutsV2PostAuthRequest = {
      challenge: authRequest?.challenge,
      tokenResponse: authRequest?.tokenResponse,
      mfaCallbackUrl: authRequest?.mfaCallbackUrl,
    }

    submitPayoutAuthRequest(payoutsPostrequest, {
      onSuccess: () => {
        setStep(FiatSendSteps.EOF)
        queryClient.invalidateQueries({
          queryKey: reactQueryKeys.walletTransactions({
            walletId: Number(selectedWallet?.walletId),
            offset: 0,
            max: 10,
          }),
        })
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
          step: FiatSendSteps.CONFIRMATION,
          apiVersion: PAYOUT_VERSIONS.UNIFIED_PAYOUT_V2,
          error,
        })
        onErrorHandler('Error from submitPayoutAuthRequest', error, {
          payoutsPostrequest,
          payoutRequest,
        })
      },
      onSettled: (response: UnifiedPayoutsV2AuthResponse) => {
        setTransactionId(response?.id)
        setSubmitBeneficiary(false)
      },
    })
  }

  useEffect(() => {
    submitBeneficiary && onSubmitPostRequest()
  }, [submitBeneficiary])

  return {
    onSubmitPayoutHandler,
    isPayoutRequestLoading: isSubmittingUnifiedPayoutRequest,
    isPayoutAuthRequestLoading: isSubmittingUnifiedPayoutAuthRequest,
  }
}
